/**
 * Custom MSW handlers for Kinde authentication mocking
 *
 * These handlers intercept Kinde auth endpoints and return mock data
 * to allow E2E tests to run without real authentication.
 */
import { http, HttpResponse } from 'msw';

/**
 * Test user data that will be returned by mocked auth endpoints
 * Using deterministic UUID matching other handlers
 */
// SAM-1086: the API no longer serves identity attributes, so the mocked user
// must not either — a fixture that still carries them would let a component
// regress to rendering identity without any test noticing.
export const TEST_USER = {
	id: '00000000-0000-0000-0000-000000000001',
	is_active: true,
	is_superuser: false,
	timezone: 'Europe/Warsaw',
	preferred_language: 'en',
	theme_preference: 'system',
	sidebar_expanded: true,
	favorites_expanded: true,
	categories_expanded: true,
	category_display_order: [],
	sidebar_favorites: [],
	// SML-140: the default mocked user has already given health-data consent.
	health_data_consent: {
		status: 'granted',
		version: 'health-data-v1',
		granted_at: '2026-10-18T10:00:00Z',
		withdrawn_at: null,
		current_version: 'health-data-v1'
	}
};

/**
 * Mock JWT token for testing
 */
export const MOCK_TOKEN = 'mock-jwt-token-e2e-12345';

/**
 * MSW handlers for Kinde auth endpoints
 */
export const kindeHandlers = [
	// Mock token endpoint - returns fake JWT
	http.get('/api/auth/token', () => {
		return HttpResponse.json({ token: MOCK_TOKEN });
	}),

	// Mock user sync endpoint
	http.post('/api/auth/sync', () => {
		return HttpResponse.json(TEST_USER, { status: 200 });
	}),

	// Mock current user endpoint - returns test user data
	http.get('*/api/users/me', () => {
		return HttpResponse.json(TEST_USER);
	}),

	// Mock user update endpoint
	http.patch('*/api/users/me', async ({ request }) => {
		const updates = (await request.json()) as Record<string, unknown>;
		return HttpResponse.json({ ...TEST_USER, ...updates });
	}),

	// SML-140: health-data consent grant / withdrawal
	http.post('*/api/users/me/health-data-consent', async ({ request }) => {
		const { version } = (await request.json()) as { version: string };
		return HttpResponse.json({
			...TEST_USER,
			health_data_consent: {
				status: 'granted',
				version,
				granted_at: new Date().toISOString(),
				withdrawn_at: null,
				current_version: TEST_USER.health_data_consent.current_version
			}
		});
	}),

	http.post('*/api/users/me/health-data-consent/withdraw', () =>
		HttpResponse.json({
			...TEST_USER,
			health_data_consent: {
				// Mirrors the backend: withdrawal clears version and granted_at.
				...TEST_USER.health_data_consent,
				status: 'withdrawn',
				version: null,
				granted_at: null,
				withdrawn_at: new Date().toISOString()
			}
		})
	)
];
